import { useMemo } from 'react';

import { useGetMetadataQuery } from '../api/wizardsApi';
import { type ProviderMetadata, transformMetadata } from '../utils/metadataTransform';

/**
 * useWizardMetadata Hook
 *
 * Fetches and transforms provider-specific metadata for schema selection.
 *
 * Based on:
 * - wizard-framework actions/wizard.js getProviderMetadata()
 * - wizard-framework metadataFunctions transformations
 *
 * Features:
 * - Automatic RTK Query caching
 * - Provider-specific transformations (Salesforce, Canvas, Blackboard)
 * - Loading states
 */
export const useWizardMetadata = (connectionKey: string, providerType: string, skip?: boolean) => {
  const { data: rawMetadata, isLoading, error } = useGetMetadataQuery(connectionKey, { skip });

  const transformedMetadata = useMemo(() => {
    if (!rawMetadata) return null;

    return transformMetadata(rawMetadata as ProviderMetadata, providerType);
  }, [rawMetadata, providerType]);

  return {
    metadata: transformedMetadata,
    isLoading,
    error,
  };
};
